import {useQuery} from '@tanstack/react-query';
import {API, type VerificationSettings} from '@chekin/api';
import {QUERY_CACHE_KEYS, type GuestGroup} from '@chekin/core';

type Params = {
  housingId?: string;
  guestGroup?: GuestGroup;
};

export function useVerificationSettings({housingId, guestGroup}: Params) {
  const isLeadGuest = !guestGroup?.added_number_of_guests;

  const {data, isLoading} = useQuery<VerificationSettings[] | undefined>({
    queryKey: [QUERY_CACHE_KEYS.verificationSettings, housingId],
    queryFn: () => API.verification.getSettings(housingId!),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(housingId && isLeadGuest),
  });

  const isEmailEnabled = data?.[0]?.require_email_otp;
  const isPhoneEnabled = data?.[0]?.require_phone_otp;
  const isVerified = !!guestGroup?.is_leader_contact_verified;

  const isContactVerificationRequired = Boolean(
    isLeadGuest && (isEmailEnabled || isPhoneEnabled),
  );

  const isNeedVerification = isContactVerificationRequired && !isVerified;

  if (!isNeedVerification) {
    return {
      isEmailEnabled: false,
      isPhoneEnabled: false,
      isLoading,
      isNeedVerification: false,
      isContactVerificationRequired,
    };
  }

  return {
    isEmailEnabled,
    isPhoneEnabled,
    isLoading,
    isNeedVerification,
    isContactVerificationRequired,
  };
}
